import type { User, Project, Asset, InsertUser, InsertProject, InsertAsset, MarketplaceListing, InsertMarketplaceListing, MarketplaceOrder } from "@shared/schema";
import type { AssetTag, SyncPayload } from "@/types/asset-tags";

const API_BASE = "/api";

// Custom error so callers can distinguish 404 (truly missing) from 5xx /
// network blips. Critical: ComicCreator (and other creators) use this to
// decide whether to show "project not found" — without the status, every
// transient server hiccup wiped students' in-memory work.
export class ApiError extends Error {
  status: number;
  body?: any;
  constructor(message: string, status: number, body?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Network error" }));
    throw new ApiError(error.message || "Request failed", response.status, error);
  }
  return response.json();
}

interface AuthResponse {
  id: string;
  email: string;
  name: string;
  role: string;
  accountType: string;
  xp: number;
  level: number;
  totalMinutes: number;
}

export const authApi = {
  signup: async (data: { email: string; password: string; name: string; dateOfBirth: string; parentalConsent?: boolean }) => {
    const response = await fetch(`${API_BASE}/auth/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    return handleResponse<AuthResponse>(response);
  },

  login: async (email: string, password: string) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
      credentials: "include",
    });
    return handleResponse<AuthResponse>(response);
  },

  logout: async () => {
    const response = await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    return handleResponse<{ message: string }>(response);
  },

  me: async () => {
    const response = await fetch(`${API_BASE}/auth/me`, {
      credentials: "include",
    });
    return handleResponse<AuthResponse>(response);
  },
  
  adminLogin: async (email: string, password: string) => {
    const response = await fetch(`${API_BASE}/auth/admin-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
      credentials: "include",
    });
    return handleResponse<AuthResponse>(response);
  },
  
  getLegalStatus: async () => {
    const response = await fetch(`${API_BASE}/auth/legal-status`, {
      credentials: "include",
    });
    return handleResponse<{ ipDisclosureAccepted: Date | null; userAgreementAccepted: Date | null; aiConsentAcceptedAt?: Date | null }>(response);
  },
  
  acceptIpDisclosure: async () => {
    const response = await fetch(`${API_BASE}/auth/accept-ip-disclosure`, {
      method: "POST",
      credentials: "include",
    });
    return handleResponse<{ ipDisclosureAccepted: Date | null; userAgreementAccepted: Date | null }>(response);
  },
  
  acceptUserAgreement: async () => {
    const response = await fetch(`${API_BASE}/auth/accept-user-agreement`, {
      method: "POST",
      credentials: "include",
    });
    return handleResponse<{ ipDisclosureAccepted: Date | null; userAgreementAccepted: Date | null }>(response);
  },

  acceptAiConsent: async () => {
    const response = await fetch(`${API_BASE}/auth/accept-ai-consent`, {
      method: "POST",
      credentials: "include",
    });
    return handleResponse<{ aiConsentAcceptedAt: Date | null }>(response);
  },
};

export const blocksApi = {
  blockUser: async (userId: string, reason?: string) => {
    const response = await fetch(`${API_BASE}/users/${userId}/block`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(reason ? { reason } : {}),
      credentials: "include",
    });
    return handleResponse<{ success: boolean }>(response);
  },
  unblockUser: async (userId: string) => {
    const response = await fetch(`${API_BASE}/users/${userId}/block`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse<{ success: boolean }>(response);
  },
  getBlocks: async () => {
    const response = await fetch(`${API_BASE}/users/me/blocks`, {
      credentials: "include",
    });
    return handleResponse<Array<{
      id: string;
      blockedId: string;
      reason: string | null;
      createdAt: string;
      user: { id: string; name: string; email: string; avatar: string | null };
    }>>(response);
  },
  getBlockStatus: async (userId: string) => {
    const response = await fetch(`${API_BASE}/users/${userId}/block-status`, {
      credentials: "include",
    });
    return handleResponse<{ blocked: boolean }>(response);
  },
};

export const projectsApi = {
  getAll: async (lightweight = false) => {
    const url = lightweight ? `${API_BASE}/projects?fields=meta` : `${API_BASE}/projects`;
    const response = await fetch(url, {
      credentials: "include",
    });
    return handleResponse<Project[]>(response);
  },

  getOne: async (id: string) => {
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      credentials: "include",
    });
    return handleResponse<Project>(response);
  },

  create: async (data: Omit<InsertProject, "userId">) => {
    const response = await fetch(`${API_BASE}/projects`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    return handleResponse<Project>(response);
  },

  update: async (id: string, data: Partial<Omit<InsertProject, "userId">>) => {
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    return handleResponse<Project>(response);
  },

  delete: async (id: string) => {
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse<{ message: string }>(response);
  },
};

export const assetsApi = {
  getAll: async (projectId?: string) => {
    const url = projectId
      ? `${API_BASE}/assets?projectId=${projectId}`
      : `${API_BASE}/assets`;
    const response = await fetch(url, {
      credentials: "include",
    });
    return handleResponse<Asset[]>(response);
  },

  create: async (data: Omit<InsertAsset, "userId">) => {
    const response = await fetch(`${API_BASE}/assets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    return handleResponse<Asset>(response);
  },

  delete: async (id: string) => {
    const response = await fetch(`${API_BASE}/assets/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse<{ message: string }>(response);
  },
};

export const adminApi = {
  getUsers: async () => {
    const response = await fetch(`${API_BASE}/admin/users`, {
      credentials: "include",
    });
    return handleResponse<Array<{
      id: string;
      email: string;
      name: string;
      role: string;
      createdAt: Date;
    }>>(response);
  },

  getProjects: async () => {
    const response = await fetch(`${API_BASE}/admin/projects`, {
      credentials: "include",
    });
    return handleResponse<Project[]>(response);
  },

  getStats: async () => {
    const response = await fetch(`${API_BASE}/admin/stats`, {
      credentials: "include",
    });
    return handleResponse<{
      totalUsers: number;
      totalProjects: number;
      projectsByType: { type: string; count: number }[];
    }>(response);
  },
};

export interface Announcement {
  id: string;
  userId: string;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  linkUrl?: string | null;
  linkText?: string | null;
  eventType: string;
  isFeatured: boolean;
  isActive: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

export const announcementsApi = {
  getActive: async (featuredOnly?: boolean) => {
    const url = featuredOnly 
      ? `${API_BASE}/announcements/active?featured=true` 
      : `${API_BASE}/announcements/active`;
    const response = await fetch(url);
    return handleResponse<Announcement[]>(response);
  },

  getAll: async (featuredOnly?: boolean) => {
    const url = featuredOnly 
      ? `${API_BASE}/announcements?featured=true` 
      : `${API_BASE}/announcements`;
    const response = await fetch(url, { credentials: "include" });
    return handleResponse<Announcement[]>(response);
  },

  getMine: async () => {
    const response = await fetch(`${API_BASE}/announcements/mine`, {
      credentials: "include",
    });
    return handleResponse<Announcement[]>(response);
  },

  getOne: async (id: string) => {
    const response = await fetch(`${API_BASE}/announcements/${id}`);
    return handleResponse<Announcement>(response);
  },

  create: async (data: Partial<Announcement>) => {
    const response = await fetch(`${API_BASE}/announcements`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    return handleResponse<Announcement>(response);
  },

  update: async (id: string, data: Partial<Announcement>) => {
    const response = await fetch(`${API_BASE}/announcements/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    return handleResponse<Announcement>(response);
  },

  delete: async (id: string) => {
    const response = await fetch(`${API_BASE}/announcements/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse<{ success: boolean }>(response);
  },
};

export const marketplaceApi = {
  getListings: async (filters?: { type?: string; search?: string; limit?: number; offset?: number; pricing?: string }) => {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));
    if (filters?.pricing) params.set('pricing', filters.pricing);
    const response = await fetch(`${API_BASE}/marketplace/listings?${params}`, { credentials: "include" });
    return handleResponse<MarketplaceListing[]>(response);
  },

  getListing: async (id: string) => {
    const response = await fetch(`${API_BASE}/marketplace/listings/${id}`, { credentials: "include" });
    return handleResponse<MarketplaceListing>(response);
  },

  getMyListings: async () => {
    const response = await fetch(`${API_BASE}/marketplace/my-listings`, { credentials: "include" });
    return handleResponse<MarketplaceListing[]>(response);
  },

  createListing: async (data: Partial<InsertMarketplaceListing>) => {
    const response = await fetch(`${API_BASE}/marketplace/listings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    return handleResponse<MarketplaceListing>(response);
  },

  updateListing: async (id: string, data: Partial<InsertMarketplaceListing>) => {
    const response = await fetch(`${API_BASE}/marketplace/listings/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
      credentials: "include",
    });
    return handleResponse<MarketplaceListing>(response);
  },

  deleteListing: async (id: string) => {
    const response = await fetch(`${API_BASE}/marketplace/listings/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse<{ message: string }>(response);
  },

  checkout: async (listingId: string) => {
    const response = await fetch(`${API_BASE}/marketplace/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ listingId }),
      credentials: "include",
    });
    return handleResponse<{ url: string }>(response);
  },

  verifyPurchase: async (sessionId: string) => {
    const response = await fetch(`${API_BASE}/marketplace/verify-purchase`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId }),
      credentials: "include",
    });
    return handleResponse<{ success: boolean; orderId: string }>(response);
  },

  getPurchases: async () => {
    const response = await fetch(`${API_BASE}/marketplace/purchases`, { credentials: "include" });
    return handleResponse<MarketplaceOrder[]>(response);
  },

  getEarnings: async () => {
    const response = await fetch(`${API_BASE}/marketplace/earnings`, { credentials: "include" });
    return handleResponse<{ orders: MarketplaceOrder[]; totalEarnings: number }>(response);
  },

  claimFree: async (listingId: string) => {
    const response = await fetch(`${API_BASE}/marketplace/claim-free`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ listingId }),
      credentials: "include",
    });
    return handleResponse<{ success: boolean; orderId: string }>(response);
  },

  getDownload: async (listingId: string) => {
    const response = await fetch(`${API_BASE}/marketplace/listings/${listingId}/download`, { credentials: "include" });
    return handleResponse<any>(response);
  },
};

export interface FxEffect {
  id: string;
  name: string;
  description: string;
  type: string;
  layer_count: number;
  preview_data_url: string;
  canvas_background: string;
  total_frames: number;
  fps: number;
  created_at: string;
  updated_at: string;
  layers?: any[];
  asset_tag?: AssetTag;
  target_page?: number;
  project_id?: string;
  source_mode?: string;
  source_panel_id?: string;
  metadata?: Record<string, any>;
  mode_hints?: Record<string, any>;
  script_data?: any;
  user_email?: string;
  user_name?: string;
  synced_to_cloud?: boolean;
}

export const fxStudioApi = {
  listEffects: async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${API_BASE}/fx-studio/effects`, { credentials: "include", signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch {
      clearTimeout(timeoutId);
      return [];
    }
  },

  getEffect: async (id: string) => {
    const response = await fetch(`${API_BASE}/fx-studio/effects/${id}`, { credentials: "include" });
    return handleResponse<FxEffect>(response);
  },

  pushEffect: async (effect: { name: string; layers: any[]; canvas_background?: string; preview_data_url?: string }) => {
    const response = await fetch(`${API_BASE}/fx-studio/effects`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(effect),
      credentials: "include",
    });
    return handleResponse<any>(response);
  },

  updateEffect: async (id: string, updates: Record<string, any>) => {
    const response = await fetch(`${API_BASE}/fx-studio/effects/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
      credentials: "include",
    });
    return handleResponse<any>(response);
  },

  deleteEffect: async (id: string) => {
    const response = await fetch(`${API_BASE}/fx-studio/effects/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return handleResponse<any>(response);
  },

  pushTaggedAsset: async (payload: SyncPayload) => {
    const response = await fetch(`${API_BASE}/fx-studio/effects`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: payload.name,
        description: payload.description,
        preview_data_url: payload.preview_data_url,
        asset_tag: payload.asset_tag,
        target_page: payload.target_page,
        project_id: payload.project_id,
        source_mode: payload.source_mode,
        source_panel_id: payload.source_panel_id,
        type: payload.type || "static-asset",
        layers: payload.layers || [],
        canvas_background: payload.canvas_background,
        metadata: payload.metadata,
        total_frames: payload.total_frames,
        fps: payload.fps,
        layer_count: payload.layer_count,
        mode_hints: payload.mode_hints,
        script_data: payload.script_data,
      }),
      credentials: "include",
    });
    return handleResponse<any>(response);
  },

  syncToCloud: async (effectId: string) => {
    const response = await fetch(`${API_BASE}/fx-studio/effects/${effectId}/sync-to-cloud`, {
      method: "POST",
      credentials: "include",
    });
    return handleResponse<any>(response);
  },

  listByTag: async (tag?: AssetTag, projectId?: string) => {
    const params = new URLSearchParams();
    if (tag) params.set("asset_tag", tag);
    if (projectId) params.set("project_id", projectId);
    const qs = params.toString();
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(`${API_BASE}/fx-studio/effects${qs ? `?${qs}` : ""}`, { credentials: "include", signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch {
      clearTimeout(timeoutId);
      return [];
    }
  },

  healthCheck: async () => {
    const response = await fetch(`${API_BASE}/fx-studio/health`);
    return handleResponse<{ status: string; timestamp: string; upstream: string }>(response);
  },
};
